import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ImagePlus, X } from "lucide-react";
import { api, L, errMsg, useFetch, useTitle } from "../lib";
import { PageHeader, Field, useHuman, Notice } from "../ui";

const MAX = 10;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

function FilePicker({ files, setFiles, captions, setCaptions, withCaptions }) {
  const [err, setErr] = useState("");
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    const bad = picked.find((f) => !TYPES.includes(f.type) || f.size > 15 * 1024 * 1024);
    setErr(bad ? `“${bad.name}” isn't a JPG, PNG or WEBP under 15 MB.` : "");
    const ok = picked.filter((f) => TYPES.includes(f.type) && f.size <= 15 * 1024 * 1024);
    setFiles([...files, ...ok].slice(0, MAX));
    e.target.value = "";
  };
  const remove = (i) => { setFiles(files.filter((_, j) => j !== i)); setCaptions(captions.filter((_, j) => j !== i)); };
  return (
    <div>
      <label className="lou-btn-ghost cursor-pointer" data-testid="file-picker-label">
        <ImagePlus size={18} /> Choose photographs
        <input type="file" accept={TYPES.join(",")} multiple className="sr-only" onChange={add} data-testid="file-picker-input" />
      </label>
      <p className="text-xs text-lou-soft mt-2">Up to {MAX} photographs · JPG, PNG or WEBP · 15 MB each. Location data is removed from the copies shown on the site.</p>
      {err && <p className="text-sm text-red-700 mt-2" role="alert">{err}</p>}
      {files.length > 0 && (
        <ul className="mt-4 grid sm:grid-cols-2 gap-4">
          {files.map((f, i) => (
            <li key={i} className="flex gap-3 items-start bg-lou-paper border border-lou-line p-3" data-testid={`file-item-${i}`}>
              <img src={previews[i]} alt="" className="w-20 h-20 object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs truncate text-lou-soft">{f.name}</p>
                {withCaptions && (
                  <input className="lou-input !py-2 mt-1 text-sm" placeholder="Caption (optional)" aria-label={`Caption for ${f.name}`}
                    value={captions[i] || ""} data-testid={`file-caption-${i}`}
                    onChange={(e) => { const c = [...captions]; c[i] = e.target.value; setCaptions(c); }} />
                )}
              </div>
              <button type="button" onClick={() => remove(i)} aria-label={`Remove ${f.name}`} className="p-1 hover:text-lou-clay"><X size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Visibility({ value, onChange }) {
  return (
    <fieldset>
      <legend className="lou-label">Who may see this?</legend>
      <div className="grid sm:grid-cols-2 gap-3">
        {[["public", "Share it on the site", "It appears for visitors, under your name."],
          ["private", "Keep it for the family", "Only the family will see it."]].map(([v, t, d]) => (
          <label key={v} className={`cursor-pointer border p-4 transition-colors ${value === v ? "border-lou-clay bg-lou-paper" : "border-lou-line hover:border-lou-clay/60"}`}>
            <input type="radio" name="visibility" value={v} checked={value === v} onChange={() => onChange(v)} className="mr-2 accent-[#4A4592]" data-testid={`visibility-${v}`} required />
            <span className="font-medium">{t}</span>
            <span className="block text-xs text-lou-soft mt-1 ml-5">{d}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function useSubmit(path, build, human) {
  const [state, setState] = useState({ sending: false, done: null, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ sending: true, done: null, error: "" });
    try {
      const fd = build();
      Object.entries(human.values).forEach(([k, v]) => fd.append(k, v));
      const r = await api.post(path, fd);
      setState({ sending: false, done: r.data, error: "" });
    } catch (err) {
      setState({ sending: false, done: null, error: errMsg(err) });
      human.reset();
    }
  };
  return [state, submit];
}

const Done = ({ status, what }) => (
  <Notice testid="share-success">
    Thank you. {status === "approved"
      ? <>{what === "memory" ? "Your memory is" : "Your photographs are"} now part of the archive. <Link to={L(what === "memory" ? "/remembering" : "/photographs")} className="underline">See {what === "memory" ? "it" : "them"} here</Link>.</>
      : <>{what === "memory" ? "Your memory has" : "Your photographs have"} been kept privately for the family.</>}
  </Notice>
);

function MemoryForm() {
  const human = useHuman();
  const [f, setF] = useState({ display_name: "", email: "", relationship: "", title: "", body: "", date_text: "", location: "", visibility: "" });
  const [files, setFiles] = useState([]);
  const [caps, setCaps] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [state, submit] = useSubmit("/submit/story", () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    files.forEach((x) => fd.append("files", x));
    fd.append("captions", JSON.stringify(files.map((_, i) => caps[i] || "")));
    return fd;
  }, human);
  if (state.done) return <Done status={state.done.status} what="memory" />;
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="memory-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="m-name" label="Your name, as it should appear" required value={f.display_name} onChange={set("display_name")} data-testid="memory-name" />
        <Field id="m-email" label="Email (never published)" type="email" required value={f.email} onChange={set("email")} data-testid="memory-email" />
        <Field id="m-rel" label="How you knew Louise (optional)" placeholder="e.g. neighbour, student, cousin" value={f.relationship} onChange={set("relationship")} data-testid="memory-relationship" />
        <Field id="m-title" label="Title (optional)" value={f.title} onChange={set("title")} data-testid="memory-title" />
      </div>
      <Field id="m-body" label="Your memory" textarea required hint="A line or a long story, whatever feels right." value={f.body} onChange={set("body")} data-testid="memory-body" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="m-date" label="Roughly when (optional)" placeholder="e.g. summer 1985, the early 90s" value={f.date_text} onChange={set("date_text")} data-testid="memory-date" />
        <Field id="m-loc" label="Where (optional)" value={f.location} onChange={set("location")} data-testid="memory-location" />
      </div>
      <FilePicker files={files} setFiles={setFiles} captions={caps} setCaptions={setCaps} withCaptions />
      <Visibility value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} />
      {human.ui}
      {state.error && <Notice kind="error" testid="share-error">{state.error}</Notice>}
      <button className="lou-btn" disabled={state.sending} data-testid="memory-submit">{state.sending ? "Sharing…" : "Share this memory"}</button>
    </form>
  );
}

function PhotoForm() {
  const human = useHuman();
  const { data: albums } = useFetch("/albums");
  const [f, setF] = useState({ uploader_name: "", uploader_email: "", album_id: "", photographer_credit: "", date_text: "", location: "", people: "", story: "", visibility: "" });
  const [files, setFiles] = useState([]);
  const [caps, setCaps] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [state, submit] = useSubmit("/submit/photos", () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("captions", JSON.stringify(files.map((_, i) => caps[i] || "")));
    files.forEach((x) => fd.append("files", x));
    return fd;
  }, human);
  if (state.done) return <Done status={state.done.status} what="photographs" />;
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="photo-form">
      <FilePicker files={files} setFiles={setFiles} captions={caps} setCaptions={setCaps} withCaptions />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="p-name" label="Your name" required value={f.uploader_name} onChange={set("uploader_name")} data-testid="photo-uploader-name" />
        <Field id="p-email" label="Email (never published)" type="email" required value={f.uploader_email} onChange={set("uploader_email")} data-testid="photo-uploader-email" />
        <Field id="p-cred" label="Who took the photograph? (optional)" hint="Leave blank if you don't know." value={f.photographer_credit} onChange={set("photographer_credit")} data-testid="photo-credit" />
        <div><label htmlFor="p-album" className="lou-label">Album (optional)</label>
          <select id="p-album" className="lou-input" value={f.album_id} onChange={set("album_id")} data-testid="photo-album">
            <option value="">Let the family decide</option>
            {(albums || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select></div>
        <Field id="p-date" label="Roughly when (optional)" value={f.date_text} onChange={set("date_text")} data-testid="photo-date" />
        <Field id="p-loc" label="Where (optional)" value={f.location} onChange={set("location")} data-testid="photo-location" />
      </div>
      <Field id="p-people" label="Who is pictured (optional)" value={f.people} onChange={set("people")} data-testid="photo-people" />
      <Field id="p-story" label="The story behind them (optional)" textarea value={f.story} onChange={set("story")} data-testid="photo-story" />
      <Visibility value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} />
      {human.ui}
      {state.error && <Notice kind="error" testid="share-error">{state.error}</Notice>}
      <button className="lou-btn" disabled={state.sending || !files.length} data-testid="photo-submit">{state.sending ? "Uploading…" : `Share ${files.length || ""} photograph${files.length === 1 ? "" : "s"}`}</button>
    </form>
  );
}

export default function Share() {
  useTitle("Share a memory");
  const [params, setParams] = useSearchParams();
  const tab = params.get("type") === "photos" ? "photos" : "memory";
  return (
    <div data-testid="share-page">
      <PageHeader eyebrow="Share" title="Share a memory, a photograph, or a moment with Louise."
        intro="No account needed. Your name appears with what you share; your email stays private and is only used if the family needs to reach you. The family looks after this archive and may gently edit, move or remove contributions." />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div role="tablist" className="flex gap-8 border-b border-lou-line mb-10">
          {[["memory", "A memory or story"], ["photos", "Photographs"]].map(([k, t]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "photos" ? { type: "photos" } : {})}
              data-testid={`share-tab-${k}`}
              className={`font-lou text-xl sm:text-2xl pb-3 -mb-px border-b-2 transition-colors ${tab === k ? "border-lou-clay" : "border-transparent text-lou-soft hover:text-lou-ink"}`}>{t}</button>
          ))}
        </div>
        {tab === "memory" ? <MemoryForm /> : <PhotoForm />}
      </div>
    </div>
  );
}
